/**
 * WebSocket Message Types and Interfaces
 * Based on WS_API_CONTRACT.md
 */

export const MessageType = {
    AUTH: "auth",
    CONFIGURE_PROVIDER: "configure_provider",
    SUBSCRIBE: "subscribe",
    UNSUBSCRIBE: "unsubscribe",
    TICKS: "ticks",
    DATA: "data",
    ERROR: "error",
    SUCCESS: "success",
    PING: "ping",
    PONG: "pong",
    PROVIDER_STREAM_CONNECTED:"provider_stream_connected",
    PROVIDER_STREAM_DISCONNECTED:"provider_stream_disconnected"

} as const;

export type MessageType = typeof MessageType[keyof typeof MessageType];

// Base message interface
export interface BaseMessage {
    type: MessageType;
    timestamp?: number;
}

// Client to Server Messages
export interface AuthMessage extends BaseMessage {
    type: typeof MessageType.AUTH;
    token: string;
}

export interface ProviderCredentials {
    apiKey?: string;
    apiSecret?: string;
    accessToken?: string;
    [key: string]: any;
}

export interface ConfigureProviderMessage extends BaseMessage {
    type: typeof MessageType.CONFIGURE_PROVIDER;
    providerType: string;
    credentials: ProviderCredentials;
}

export interface SubscribeMessage extends BaseMessage {
    type: typeof MessageType.SUBSCRIBE;
    channels: string[];
}

export interface UnsubscribeMessage extends BaseMessage {
    type: typeof MessageType.UNSUBSCRIBE;
    channels: string[];
}

export interface PingMessage extends BaseMessage {
    type: typeof MessageType.PING;
}

// Server to Client Messages
export interface SuccessMessage extends BaseMessage {
    type: typeof MessageType.SUCCESS;
    message: string;
    data?: any;
    timestamp: number;
}

export interface ErrorMessage extends BaseMessage {
    type: typeof MessageType.ERROR;
    error: string;
    details?: any;
    timestamp: number;
}

export interface DataMessage extends BaseMessage {
    type: typeof MessageType.DATA;
    channel: string;
    data: any;
    timestamp: number;
}

export interface PongMessage extends BaseMessage {
    type: typeof MessageType.PONG;
    timestamp: number;
}

export interface ProviderStreamMessage extends BaseMessage {
    type: typeof MessageType.PROVIDER_STREAM_CONNECTED | typeof MessageType.PROVIDER_STREAM_DISCONNECTED;
    message: string;
    timestamp: number;
}

// Union types
export type ClientMessage =
    | AuthMessage
    | ConfigureProviderMessage
    | SubscribeMessage
    | UnsubscribeMessage
    | PingMessage
    | any; // For future extensibility

export type ServerMessage =
    | SuccessMessage
    | ErrorMessage
    | DataMessage
    | PongMessage
    | ProviderStreamMessage
    | any;

// WebSocket connection states
export const ConnectionState = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    AUTHENTICATED: 'authenticated',
    ERROR: 'error'
} as const;

export type ConnectionState = typeof ConnectionState[keyof typeof ConnectionState];
